/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {ReactElement, useCallback} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {getValidElementId} from '@ion/core/src/utils/StringUtil';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {BatteryIcon} from '../../icons/BatteryIcon';
import {ChargingIcon} from '../../icons/ChargingIcon';
import {Color, DLSTheme} from '../../types/types';
import ChargeLevel from '../chargelevel/ChargeLevel';

// defines the prop definitions for the badge
export interface BatteryChargeLevelProps {
  isCharging?: boolean;
  value?: number;
  iconId?: string;
  valueComponent: ReactElement;
  isCardVariant?: boolean;
};

const useStyles = makeStyles((theme: DLSTheme) => {
  return {
    mainContainer : {
      display    : 'flex',
      alignItems : 'center'
    },
    iconContainer : {
      paddingTop : 2
    },
    valueContainer : {
      paddingLeft : 5
    }
  };
});

/**
 * Defines the BatteryChargeLevel component. This component is reusable and can be custom
 * rendered with props.
 *
 * ```typescript
 *
 *   import BatteryChargeLevel from '@ion/dls/src/components/battery'
 *   <BatteryChargeLevel value={value}
 *     iconId={iconId}
 *     valueComponent={valueComponent} />
 * ```
 */
export const BatteryChargeLevel = ({
  isCharging = false,
  value = 0,
  iconId = getValidElementId(null),
  valueComponent,
  isCardVariant = false
}: BatteryChargeLevelProps) => {
  const classes = useStyles();

  const getBatteryLevelColor = useCallback((value: number) => {
    if (value > 50) {
      return dlsColor('caGreen');
    }
    if (value > 25) {
      return dlsColor('caYellow');
    }
    return dlsColor('caRed');
  }, []);
  
  if (!isCardVariant) {
    return (
      <div className={classes.mainContainer}>
        <div className={classes.iconContainer}>
          {!isCharging
            ? <BatteryIcon batteryPercentage={Math.round(value)}
              color={getBatteryLevelColor(value)}
              id={iconId} />
            : <ChargingIcon />}
        </div>
        <div className={classes.valueContainer}>
          {valueComponent}
        </div>
      </div>
    );
  }
  return (
    <div className={classes.mainContainer}>
      <ChargeLevel charge={value} isCardVariant={isCardVariant} />
    </div>
  );
};

export default BatteryChargeLevel;
